import React, { useState } from 'react'
import { formatCurrency } from '../../utils/api'
import DealLink from '../shared/DealLink.jsx'
import DecisionDialog from './DecisionDialog.jsx'

const RETURN_REQUESTED = 'return_requested'
const NO_COURIER_LABEL = 'Asked for it back, no courier booked'

/** The one action an item offers: List it when we owe money, Bin it when we owe nothing. */
export function actionFor(item) {
  if (item.revisedOffer == null) return null
  return item.revisedOffer > 0 ? 'list' : 'bin'
}

/**
 * The Decision due tab of Issue Trades (spec 20261103): quotes whose 14 days
 * have passed with no reply. Each item shows its revised offer and one action.
 * The list is Prism's decision_due queue, not a date worked out here.
 *
 * `onOpenDeal(quoteId)` opens the full deal for a late reply (it is only offered
 * for a quote the Issue Trades list holds). `onMoved(warnings)` runs once an
 * item has moved, so the screen can reload.
 */
function DecisionDue({ deals, onOpenDeal, onMoved }) {
  const [deciding, setDeciding] = useState(null) // { deal, item, action }

  function handleDone(warnings) {
    setDeciding(null)
    onMoved(warnings)
  }

  return (
    <div>
      {deals.map(deal => (
        <div className="card" key={deal.quoteId} style={{ marginBottom: 'var(--space-4)' }}>
          <div className="card-header">
            <h3 className="card-title">{deal.customerName || deal.customerEmail || 'Customer'}</h3>
            <DealLink dealId={deal.quoteId} reference={deal.reference} newTab />
          </div>
          <div style={{ padding: 'var(--space-4)' }}>
            {deal.items.map(item => (
              <DecisionItemRow
                key={item.quoteItemId}
                item={item}
                onDecide={(action) => setDeciding({ deal, item, action })}
              />
            ))}
            {onOpenDeal && (
              <button className="btn btn-sm btn-secondary" onClick={() => onOpenDeal(deal.quoteId)}>
                The seller replied: open the deal
              </button>
            )}
          </div>
        </div>
      ))}

      {deciding && (
        <DecisionDialog
          dealId={deciding.deal.quoteId}
          item={deciding.item}
          action={deciding.action}
          onDone={handleDone}
          onClose={() => setDeciding(null)}
        />
      )}
    </div>
  )
}

function DecisionItemRow({ item, onDecide }) {
  const action = actionFor(item)
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12, flexWrap: 'wrap', padding: '8px 0',
        borderTop: '1px solid var(--border, #E7E5E4)'
      }}
    >
      <div>
        <div style={{ fontWeight: 600 }}>{item.name}</div>
        {item.state === RETURN_REQUESTED && (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>{NO_COURIER_LABEL}</div>
        )}
        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
          {item.revisedOffer == null
            ? 'No revised offer on file. Check the deal.'
            : `Revised offer ${formatCurrency(item.revisedOffer)}`}
        </div>
      </div>
      {action && (
        <button
          className={`btn btn-sm ${action === 'list' ? 'btn-success' : 'btn-danger'}`}
          onClick={() => onDecide(action)}
        >
          {action === 'list' ? 'List it' : 'Bin it'}
        </button>
      )}
    </div>
  )
}

export default DecisionDue
